import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button'
import { DataQualityTagItem } from '@/hooks/api/use-table-details-query'
import { copy } from '@/lib/utils'
import { saveAs } from 'file-saver'
import { Copy, Download, FileCode, FileX } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { renderFrameworkCitations } from './tables-common-cells'

interface DataQualityRuleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  dataQualityTags: DataQualityTagItem[]
  columnName: string
}

export const DataQualityRuleDialog = ({
  open,
  onOpenChange,
  dataQualityTags,
  columnName,
}: DataQualityRuleDialogProps) => {
  const [selectedScripts, setSelectedScripts] = useState<Set<number>>(new Set())

  // Memoize scripts with SQL to avoid recalculation on every render
  const scriptsWithSQL = useMemo(
    () => dataQualityTags.filter((tag) => tag.dq_sql_script),
    [dataQualityTags],
  )

  const totalScripts = scriptsWithSQL.length

  // Reset selection when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedScripts(new Set())
    }
  }, [open])

  const handleCopySQL = (sql: string) => {
    copy({
      text: sql,
      successMessage: 'SQL Script copied to clipboard!',
      errorMessage: 'Failed to copy SQL script',
    })
  }

  const handleDownloadSQL = (sql: string, tagName: string) => {
    const blob = new Blob([sql], { type: 'text/sql;charset=utf-8' })
    const filename = `${tagName.replaceAll(/\s+/g, '-').toLowerCase()}.sql`
    saveAs(blob, filename)
  }

  const handleSelectAll = (checked: boolean) => {
    setSelectedScripts(checked ? new Set(scriptsWithSQL.map((_, idx) => idx)) : new Set())
  }

  const handleSelectScript = (index: number, checked: boolean) => {
    setSelectedScripts((prev) => {
      const next = new Set(prev)
      checked ? next.add(index) : next.delete(index)
      return next
    })
  }

  const handleBulkDownload = () => {
    if (selectedScripts.size === 0) return

    const combinedSQL = Array.from(selectedScripts)
      .sort((a, b) => a - b) // Maintain consistent order
      .map((index) => {
        const tag = scriptsWithSQL[index]
        return `-- ${tag.tag} \n${tag.dq_sql_script}\n`
      })
      .join('\n-- ============================================\n\n')

    const blob = new Blob([combinedSQL], { type: 'text/sql;charset=utf-8' })
    const filename = `${columnName}-data-quality-scripts.sql`
    saveAs(blob, filename)
  }

  const allSelected = totalScripts > 0 && selectedScripts.size === totalScripts

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[80vh] max-w-3xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-xl'>
            <FileCode className='size-5' />
            {columnName} Data Quality Rules, References and SQL Scripts
          </DialogTitle>
          <DialogDescription className='text-sm text-neutral-400'>
            Comprehensive list of all data quality rules grouped by tag for the {columnName} column.
          </DialogDescription>
        </DialogHeader>

        <div className='max-w-2xl space-y-6'>
          {/* Select All Section */}
          {totalScripts > 0 && (
            <div className='flex items-center justify-between rounded-lg border bg-gray-50 p-4'>
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='select-all'
                  variant='neutral'
                  checked={allSelected}
                  showUnselectIcon={allSelected}
                  onCheckedChange={handleSelectAll}
                />
                <label htmlFor='select-all' className='cursor-pointer text-sm font-medium'>
                  Select All ({selectedScripts.size} of {totalScripts})
                </label>
              </div>
              <Button
                variant='black-outline'
                size='sm'
                onClick={handleBulkDownload}
                className='flex items-center gap-2'
                disabled={selectedScripts.size === 0}
              >
                <Download className='size-4' />
                Download {selectedScripts.size > 0 && `(${selectedScripts.size})`}
              </Button>
            </div>
          )}

          {scriptsWithSQL.map((tag, scriptIndex) => {
            const isSelected = selectedScripts.has(scriptIndex)

            const getExecutionResultClassName = (result: string | undefined) => {
              if (result === 'success') return 'bg-success-50 text-success-300'
              if (result === 'error') return 'bg-error-50 text-error-300'
              return 'bg-warning-50 text-warning-300'
            }

            return (
              <div key={`${tag.tag}-${scriptIndex}`} className='space-y-4 rounded-lg border p-4'>
                <div className='flex items-center justify-between'>
                  <h3 className='text-lg font-medium'>{tag.tag}</h3>
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-medium ${getExecutionResultClassName(tag.execution_result)}`}
                  >
                    {tag.execution_result || 'pending'}
                  </span>
                </div>

                {tag.data_quality_rule && (
                  <div className='space-y-2'>
                    <h4 className='text-sm font-medium'>Rules</h4>
                    <p className='text-sm text-neutral-400'>{tag.data_quality_rule}</p>
                  </div>
                )}

                {tag.framework_citations && tag.framework_citations.length > 0 && (
                  <div className='space-y-2'>
                    <h4 className='text-sm font-medium'>References</h4>
                    <div className='space-y-3 rounded border bg-gray-50 p-3'>
                      <p className='text-sm font-medium text-neutral-500'>Frameworks:</p>
                      {renderFrameworkCitations(
                        tag.framework_citations || tag.frameworkCitations || [],
                      )}
                    </div>
                  </div>
                )}

                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center space-x-2'>
                      <Checkbox
                        variant='neutral'
                        id={`script-${scriptIndex}`}
                        checked={isSelected}
                        onCheckedChange={(checked) =>
                          handleSelectScript(scriptIndex, checked === true)
                        }
                      />
                      <label
                        htmlFor={`script-${scriptIndex}`}
                        className='cursor-pointer text-sm font-medium text-gray-700'
                      >
                        SQL Script
                      </label>
                    </div>
                    <div className='flex items-center gap-1'>
                      <TooltipIconButton
                        size='xs'
                        onClick={() => handleCopySQL(tag.dq_sql_script!)}
                        titleString='Copy SQL'
                      >
                        <Copy className='size-4' />
                      </TooltipIconButton>
                      <TooltipIconButton
                        size='xs'
                        onClick={() => handleDownloadSQL(tag.dq_sql_script!, tag.tag)}
                        titleString='Download SQL'
                      >
                        <Download className='size-4' />
                      </TooltipIconButton>
                    </div>
                  </div>
                  <div className='overflow-hidden rounded-lg border border-gray-200'>
                    <SyntaxHighlighter
                      language='sql'
                      style={oneLight}
                      wrapLines={true}
                      lineProps={{ style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' } }}
                      customStyle={{
                        borderRadius: '0.375rem',
                        padding: '1rem',
                        fontSize: '0.875rem',
                        overflowX: 'auto',
                        margin: '0',
                      }}
                    >
                      {tag.dq_sql_script!}
                    </SyntaxHighlighter>
                  </div>
                </div>
              </div>
            )
          })}

          {(dataQualityTags.length === 0 || scriptsWithSQL.length === 0) && (
            <div className='py-8 text-center text-gray-500'>
              <FileX className='mx-auto mb-4 size-12 text-gray-300' />
              <p>Run Data Quality rules to generate rules and SQL Script</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant='black-outline' onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
